import { useEffect, useState } from "react";
import {
  Loader2,
  Plus,
  Trash2,
  Save,
  Users,
  Plane,
  Clock,
  CalendarDays,
  Check,
  Ban,
} from "lucide-react";
import {
  usePoliticasRrhh,
  useUpdatePoliticasRrhh,
  useDepartamentos,
  useCreateDepartamento,
  useUpdateDepartamento,
  useDeleteDepartamento,
  useFestivos,
  useCreateFestivo,
  useDeleteFestivo,
} from "../api/rrhh";
import { useIssuers } from "@modules/billing/api/issuers";
import { useGlobalNavigationTarget } from "@shared/navigation/globalNavigation";

type Tab = "departamentos" | "vacaciones" | "fichaje" | "festivos";

const TABS: { key: Tab; label: string; icon: typeof Users }[] = [
  { key: "departamentos", label: "Departamentos", icon: Users },
  { key: "vacaciones", label: "Vacaciones", icon: Plane },
  { key: "fichaje", label: "Fichaje / jornada", icon: Clock },
  { key: "festivos", label: "Festivos", icon: CalendarDays },
];

export function RrhhConfigPage() {
  const [tab, setTab] = useState<Tab>("departamentos");

  useGlobalNavigationTarget("settings-rrhh", (target) => {
    switch (target.section) {
      case "departamentos":
      case "vacaciones":
      case "fichaje":
      case "festivos":
        setTab(target.section);
        break;
      default:
        break;
    }
  });

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <header>
        <h1 className="text-3xl font-black flex items-center gap-3 text-slate-900">
          <Users size={28} className="text-brand-700" /> Configuración · Recursos Humanos
        </h1>
        <p className="text-slate-500 mt-1">
          Ajusta departamentos, política de vacaciones, fichaje y festivos.
        </p>
      </header>

      <div className="flex gap-2 shrink-0 flex-wrap">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition ${
                tab === t.key
                  ? "bg-brand-700 text-white shadow-soft"
                  : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        {tab === "departamentos" && <Departamentos />}
        {tab === "vacaciones" && <Vacaciones />}
        {tab === "fichaje" && <Fichaje />}
        {tab === "festivos" && <Festivos />}
      </div>
    </div>
  );
}

const CARD = "bg-white rounded-2xl shadow-soft border border-slate-200/70";

// ── Departamentos ─────────────────────────────────────────────────────────
function Departamentos() {
  const { data: companies, isLoading: companiesLoading } = useIssuers();
  const [emisorId, setEmisorId] = useState<number | null>(null);
  const { data, isLoading } = useDepartamentos(emisorId);
  const create = useCreateDepartamento();
  const update = useUpdateDepartamento();
  const del = useDeleteDepartamento();
  const [nombre, setNombre] = useState("");

  useEffect(() => {
    if (emisorId !== null || !companies?.length) return;

    const inpronet = companies.find(
      (company) => company.nombre.trim().toLocaleLowerCase("es") === "inpronet solutions s.l.",
    );
    setEmisorId(inpronet?.id ?? companies[0].id);
  }, [companies, emisorId]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!emisorId || !nombre.trim()) return;
    await create.mutateAsync({ emisor_id: emisorId, nombre: nombre.trim() });
    setNombre("");
  }

  return (
    <div className={`${CARD} max-w-3xl`}>
      <div className="p-4 border-b border-slate-100">
        <label className="block max-w-md">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Empresa</span>
          <select
            value={emisorId ?? ""}
            onChange={(e) => setEmisorId(e.target.value ? Number(e.target.value) : null)}
            disabled={companiesLoading}
            className={INPUT}
          >
            <option value="">Selecciona una empresa</option>
            {(companies ?? []).map((company) => (
              <option key={company.id} value={company.id}>
                {company.nombre}
              </option>
            ))}
          </select>
        </label>
        <p className="mt-2 text-xs text-slate-400">
          Los departamentos se administran ahora dentro de la empresa seleccionada.
        </p>
      </div>

      <form onSubmit={add} className="flex gap-2 p-4 border-b border-slate-100">
        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Nuevo departamento…"
          disabled={!emisorId}
          className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400"
        />
        <button
          type="submit"
          disabled={create.isPending || !emisorId}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
        >
          <Plus size={16} /> Añadir
        </button>
      </form>

      {!emisorId ? (
        <div className="px-4 py-8 text-center text-slate-400 text-sm">Selecciona una empresa.</div>
      ) : isLoading ? (
        <div className="p-4 text-slate-500 flex items-center gap-2">
          <Loader2 className="animate-spin" size={16} /> Cargando…
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {(data ?? []).map((d) => (
            <li key={d.id} className="flex items-center justify-between px-4 py-3">
              <span className={`font-semibold ${d.activo ? "text-slate-800" : "text-slate-400 line-through"}`}>
                {d.nombre}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => update.mutate({ id: d.id, payload: { activo: !d.activo } })}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${
                    d.activo
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-slate-100 text-slate-500 border-slate-200"
                  }`}
                >
                  {d.activo ? <Check size={11} /> : <Ban size={11} />}
                  {d.activo ? "Activo" : "Inactivo"}
                </button>
                <button
                  onClick={() => {
                    if (confirm(`¿Eliminar "${d.nombre}"?`)) del.mutate(d.id);
                  }}
                  className="rounded-lg border border-slate-200 hover:bg-rose-50 hover:text-rose-600 px-2 py-1.5 text-slate-400"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </li>
          ))}
          {data && data.length === 0 && (
            <li className="px-4 py-8 text-center text-slate-400 text-sm">Sin departamentos.</li>
          )}
        </ul>
      )}
    </div>
  );
}

// ── Vacaciones ────────────────────────────────────────────────────────────
function Vacaciones() {
  const { data, isLoading } = usePoliticasRrhh();
  const update = useUpdatePoliticasRrhh();
  const [dias, setDias] = useState("22");
  const [carry, setCarry] = useState("0");
  const [limite, setLimite] = useState("03-31");
  const [aprob, setAprob] = useState("admin");
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (!data) return;
    setDias(data.rrhh_vacaciones_dias_anuales ?? "22");
    setCarry(data.rrhh_vacaciones_carry_max ?? "0");
    setLimite(data.rrhh_vacaciones_carry_limite ?? "03-31");
    setAprob(data.rrhh_vacaciones_aprobacion ?? "admin");
  }, [data]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    await update.mutateAsync({
      rrhh_vacaciones_dias_anuales: Number(dias),
      rrhh_vacaciones_carry_max: Number(carry),
      rrhh_vacaciones_carry_limite: limite,
      rrhh_vacaciones_aprobacion: aprob,
    });
    setOk(true);
    setTimeout(() => setOk(false), 2500);
  }

  if (isLoading) return <Cargando />;

  return (
    <form onSubmit={save} className={`${CARD} max-w-2xl p-6 flex flex-col gap-5`}>
      {ok && <Guardado />}
      <Campo label="Días de vacaciones por año">
        <input type="number" min={0} max={365} value={dias} onChange={(e) => setDias(e.target.value)} className={INPUT} />
      </Campo>
      <Campo label="Días máximos que se pueden arrastrar al año siguiente (carry)">
        <input type="number" min={0} max={365} value={carry} onChange={(e) => setCarry(e.target.value)} className={INPUT} />
      </Campo>
      <Campo label="Fecha límite para disfrutar los días arrastrados (MM-DD)">
        <input value={limite} onChange={(e) => setLimite(e.target.value)} placeholder="03-31" className={INPUT} />
      </Campo>
      <Campo label="Quién aprueba las vacaciones">
        <select value={aprob} onChange={(e) => setAprob(e.target.value)} className={INPUT}>
          <option value="admin">Administrador / RRHH</option>
          <option value="responsable">Responsable del empleado</option>
        </select>
      </Campo>
      <BotonGuardar pending={update.isPending} />
    </form>
  );
}

// ── Fichaje ────────────────────────────────────────────────────────────────
function Fichaje() {
  const { data, isLoading } = usePoliticasRrhh();
  const update = useUpdatePoliticasRrhh();
  const [inicio, setInicio] = useState("09:00");
  const [fin, setFin] = useState("18:00");
  const [tol, setTol] = useState("15");
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (!data) return;
    setInicio(data.rrhh_jornada_inicio ?? "09:00");
    setFin(data.rrhh_jornada_fin ?? "18:00");
    setTol(data.rrhh_fichaje_tolerancia ?? "15");
  }, [data]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    await update.mutateAsync({
      rrhh_jornada_inicio: inicio,
      rrhh_jornada_fin: fin,
      rrhh_fichaje_tolerancia: Number(tol),
    });
    setOk(true);
    setTimeout(() => setOk(false), 2500);
  }

  if (isLoading) return <Cargando />;

  return (
    <form onSubmit={save} className={`${CARD} max-w-2xl p-6 flex flex-col gap-5`}>
      {ok && <Guardado />}
      <div className="grid grid-cols-2 gap-4">
        <Campo label="Hora de entrada por defecto">
          <input type="time" value={inicio} onChange={(e) => setInicio(e.target.value)} className={INPUT} />
        </Campo>
        <Campo label="Hora de salida por defecto">
          <input type="time" value={fin} onChange={(e) => setFin(e.target.value)} className={INPUT} />
        </Campo>
      </div>
      <Campo label="Tolerancia de fichaje (minutos)">
        <input type="number" min={0} max={120} value={tol} onChange={(e) => setTol(e.target.value)} className={INPUT} />
      </Campo>
      <BotonGuardar pending={update.isPending} />
    </form>
  );
}

// ── Festivos ──────────────────────────────────────────────────────────────
function Festivos() {
  const añoActual = 2026;
  const [anio, setAnio] = useState(añoActual);
  const { data, isLoading } = useFestivos(anio);
  const create = useCreateFestivo();
  const del = useDeleteFestivo();
  const [fecha, setFecha] = useState("");
  const [nombre, setNombre] = useState("");
  const [ambito, setAmbito] = useState("nacional");

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!fecha || !nombre.trim()) return;
    await create.mutateAsync({ fecha, nombre: nombre.trim(), ambito });
    setFecha("");
    setNombre("");
  }

  return (
    <div className={`${CARD} max-w-3xl`}>
      <div className="flex items-center gap-3 p-4 border-b border-slate-100">
        <span className="text-sm font-bold text-slate-600">Año:</span>
        <select
          value={anio}
          onChange={(e) => setAnio(Number(e.target.value))}
          className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
        >
          {[añoActual - 1, añoActual, añoActual + 1].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      <form onSubmit={add} className="flex flex-wrap gap-2 p-4 border-b border-slate-100">
        <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        <input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre del festivo" className="flex-1 min-w-[160px] rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        <select value={ambito} onChange={(e) => setAmbito(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
          <option value="nacional">Nacional</option>
          <option value="autonomico">Autonómico</option>
          <option value="local">Local</option>
          <option value="empresa">Empresa</option>
        </select>
        <button type="submit" disabled={create.isPending} className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50">
          <Plus size={16} /> Añadir
        </button>
      </form>

      {isLoading ? (
        <Cargando />
      ) : (
        <ul className="divide-y divide-slate-100">
          {(data ?? []).map((f) => (
            <li key={f.id} className="flex items-center justify-between px-4 py-3">
              <div>
                <span className="font-mono font-bold text-slate-800">
                  {new Date(f.fecha).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" })}
                </span>
                <span className="ml-3 text-slate-700">{f.nombre}</span>
                <span className="ml-2 text-xs text-slate-400">({f.ambito})</span>
              </div>
              <button
                onClick={() => del.mutate(f.id)}
                className="rounded-lg border border-slate-200 hover:bg-rose-50 hover:text-rose-600 px-2 py-1.5 text-slate-400"
              >
                <Trash2 size={15} />
              </button>
            </li>
          ))}
          {data && data.length === 0 && (
            <li className="px-4 py-8 text-center text-slate-400 text-sm">Sin festivos en {anio}.</li>
          )}
        </ul>
      )}
    </div>
  );
}

// ── Helpers UI ──────────────────────────────────────────────────────────────
const INPUT = "w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm";

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}
function Cargando() {
  return (
    <div className="p-4 text-slate-500 flex items-center gap-2">
      <Loader2 className="animate-spin" size={16} /> Cargando…
    </div>
  );
}
function Guardado() {
  return (
    <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-2 text-sm">
      Cambios guardados ✓
    </div>
  );
}
function BotonGuardar({ pending }: { pending: boolean }) {
  return (
    <div className="flex justify-end">
      <button
        type="submit"
        disabled={pending}
        className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
      >
        <Save size={14} /> Guardar
      </button>
    </div>
  );
}

